/**
 * SQLPreviewPanel — Show generated pushdown SQL without executing a job run.
 *
 * Design:
 *   - "Preview SQL" button → POST .../preview-sql → renders results
 *   - Loading spinner during fetch
 *   - Metadata row: dialect badge, CTE count, column count
 *   - Source Extraction SQL, Pushdown SQL, SCD2 Insert SQL (if present) in
 *     separate scrollable monospace <pre> blocks (NFR29: horizontal scroll
 *     inside block, never the page body)
 *   - Copy-to-clipboard button per block with 1.5-second "Copied!" feedback
 *   - Disabled with inline hint when job has no column mappings
 */

import { useCallback, useState } from "react";
import { Check, Clipboard, Loader2, Search } from "lucide-react";
import type {
  PEJob,
  SQLPreviewResult,
} from "../../../features/pipeline-engineer/types";
import { previewSQL } from "../../../features/pipeline-engineer/api/pe-client";

// ---------------------------------------------------------------------------
// SQL Block
// ---------------------------------------------------------------------------

interface SQLBlockProps {
  label: string;
  sql: string;
  blockKey: string;
  copiedKey: string | null;
  onCopy: (sql: string, key: string) => void;
}

function SQLBlock({ label, sql, blockKey, copiedKey, onCopy }: SQLBlockProps) {
  const isCopied = copiedKey === blockKey;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-white/50">{label}</span>
        <button
          type="button"
          onClick={() => onCopy(sql, blockKey)}
          aria-label={
            isCopied ? `${label} copied` : `Copy ${label} to clipboard`
          }
          className="flex items-center gap-1 px-2 py-1 text-xs text-white/40 hover:text-white/70 hover:bg-white/5 rounded transition-colors"
        >
          {isCopied ? (
            <>
              <Check
                size={11}
                className="text-emerald-400"
                aria-hidden="true"
              />
              <span className="text-emerald-400">Copied!</span>
            </>
          ) : (
            <>
              <Clipboard size={11} aria-hidden="true" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre
        className="font-mono text-xs text-white/80 bg-white/5 border border-white/10 rounded-xl p-4 overflow-x-auto whitespace-pre"
        aria-label={label}
      >
        {sql}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SQLPreviewPanelProps {
  pipelineId: string;
  jobId: string;
  job: PEJob;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SQLPreviewPanel({
  pipelineId,
  jobId,
  job,
}: SQLPreviewPanelProps) {
  const [preview, setPreview] = useState<SQLPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const hasColumnMappings = job.columnMappings.length > 0;

  // ── Fetch preview ──────────────────────────────────────────────────────────
  const handlePreview = useCallback(async () => {
    setError(null);
    setPreview(null);
    setLoading(true);
    try {
      const result = await previewSQL(pipelineId, jobId);
      setPreview(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch SQL preview.",
      );
    } finally {
      setLoading(false);
    }
  }, [pipelineId, jobId]);

  // ── Copy to clipboard ──────────────────────────────────────────────────────
  const handleCopy = useCallback((sql: string, key: string) => {
    void navigator.clipboard.writeText(sql).then(
      () => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 1500);
      },
      () => {
        // Clipboard API unavailable or permission denied — silently ignore.
        // (App is HTTPS-only so this is rare; no toast needed.)
      },
    );
  }, []);

  return (
    <div className="space-y-5">
      {/* ── Trigger button ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void handlePreview()}
          disabled={!hasColumnMappings || loading}
          title={
            !hasColumnMappings
              ? "Add column mappings before previewing SQL"
              : undefined
          }
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <Search size={13} aria-hidden="true" />
          )}
          Preview SQL
        </button>

        {!hasColumnMappings && (
          <p className="text-xs text-amber-400">
            Add column mappings before previewing SQL.
          </p>
        )}
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {error}
        </p>
      )}

      {/* ── Preview result ────────────────────────────────────────────────── */}
      {preview !== null && (
        <div className="space-y-5">
          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-2 py-0.5 bg-brand-500/10 text-brand-400 border border-brand-500/20 rounded-full text-xs font-medium">
              {preview.dialect}
            </span>
            <span className="text-xs text-white/40">
              {preview.cteStepCount}{" "}
              {preview.cteStepCount === 1 ? "CTE" : "CTEs"}
            </span>
            <span className="text-white/20" aria-hidden="true">
              ·
            </span>
            <span className="text-xs text-white/40">
              {preview.columnCount}{" "}
              {preview.columnCount === 1 ? "column" : "columns"}
            </span>
          </div>

          {/* SQL blocks */}
          <SQLBlock
            label="Source Extraction SQL"
            sql={preview.sourceExtractionSql}
            blockKey="extraction"
            copiedKey={copiedKey}
            onCopy={handleCopy}
          />

          <SQLBlock
            label="Pushdown SQL"
            sql={preview.pushdownSql}
            blockKey="pushdown"
            copiedKey={copiedKey}
            onCopy={handleCopy}
          />

          {preview.scd2InsertSql !== undefined && (
            <SQLBlock
              label="SCD2 Insert SQL"
              sql={preview.scd2InsertSql}
              blockKey="scd2insert"
              copiedKey={copiedKey}
              onCopy={handleCopy}
            />
          )}
        </div>
      )}
    </div>
  );
}
